
export interface DetectedFacilities {
  allInclusive?: boolean;
  swimmingPool?: boolean;
  wifi?: boolean;
  parking?: boolean;
  restaurant?: boolean;
  bar?: boolean;
  spa?: boolean;
  fitness?: boolean;
  kidsClub?: boolean;
  beachAccess?: boolean;
  airConditioning?: boolean;
}

export const detectFacilities = (searchTerms: string[]): DetectedFacilities | null => {
  const facilities: DetectedFacilities = {};
  const fullQuery = searchTerms.join(' ').toLowerCase();
  
  console.log('🏨 === FACILITIES DETECTION ===');
  console.log('Full query for facilities detection:', fullQuery);
  
  // All-inclusive detection
  const allInclusivePatterns = [
    'all inclusive', 'all-inclusive', 'ai', 'alles inbegrepen', 
    'volledig verzorgd', 'volpension', 'all inc'
  ];
  
  if (allInclusivePatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.allInclusive = true;
    console.log('✅ Detected: All-inclusive');
  }
  
  // Swimming pool detection
  const poolPatterns = [
    'zwembad', 'pool', 'swimming', 'zwemmen', 'buitenzwembad', 
    'binnenzwembad', 'infinity pool', 'zwembaden'
  ];
  
  if (poolPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.swimmingPool = true;
    console.log('✅ Detected: Swimming pool');
  }
  
  // WiFi detection
  const wifiPatterns = ['wifi', 'wi-fi', 'internet', 'gratis wifi'];
  
  if (wifiPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.wifi = true;
    console.log('✅ Detected: WiFi');
  }
  
  // Parking detection
  const parkingPatterns = ['parkeren', 'parking', 'parkeerplaats', 'gratis parkeren'];
  
  if (parkingPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.parking = true;
    console.log('✅ Detected: Parking');
  }
  
  // Restaurant detection
  const restaurantPatterns = ['restaurant', 'eten', 'dining', 'buffet', 'diner'];
  
  if (restaurantPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.restaurant = true;
    console.log('✅ Detected: Restaurant');
  }
  
  // Bar detection
  const barPatterns = ['bar', 'lobby bar', 'poolbar', 'strandbar'];
  
  if (barPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.bar = true;
    console.log('✅ Detected: Bar');
  }
  
  // Spa/Wellness detection
  const spaPatterns = ['spa', 'wellness', 'sauna', 'massage', 'beauty', 'ontspanning'];
  
  if (spaPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.spa = true;
    console.log('✅ Detected: Spa/Wellness');
  }
  
  // Fitness detection
  const fitnessPatterns = ['fitness', 'gym', 'sportschool', 'fitnessruimte'];
  
  if (fitnessPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.fitness = true;
    console.log('✅ Detected: Fitness');
  }
  
  // Kids club detection
  const kidsPatterns = ['kinderclub', 'kids club', 'miniclub', 'kindvriendelijk'];
  
  if (kidsPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.kidsClub = true;
    console.log('✅ Detected: Kids club');
  }
  
  // Beach access detection
  const beachPatterns = ['strand', 'beach', 'strandtoegang', 'zee toegang'];
  
  if (beachPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.beachAccess = true;
    console.log('✅ Detected: Beach access');
  }
  
  // Air conditioning detection
  const aircoPatterns = ['airco', 'air conditioning', 'airconditioning', 'klimaat'];
  
  if (aircoPatterns.some(pattern => fullQuery.includes(pattern))) {
    facilities.airConditioning = true;
    console.log('✅ Detected: Air conditioning');
  }
  
  console.log('🎯 Final detected facilities:', facilities);
  return Object.keys(facilities).length > 0 ? facilities : null;
};

export const applyFacilitiesFilter = (dbQuery: any, detectedFacilities: DetectedFacilities) => {
  if (!detectedFacilities) return dbQuery;
  
  console.log('🏨 === APPLYING FACILITIES FILTERS ===');
  console.log('Facilities to apply:', detectedFacilities);
  
  const conditions: string[] = [];
  
  if (detectedFacilities.allInclusive) {
    console.log('🎯 Applying all-inclusive filter');
    conditions.push(
      `name.ilike.%all inclusive%`,
      `description.ilike.%all inclusive%`,
      `description.ilike.%alles inbegrepen%`,
      `specialCategories.ilike.%all inclusive%`,
      `typeVakantie.ilike.%all inclusive%`,
      `facilities.ilike.%all inclusive%`
    );
  }
  
  if (detectedFacilities.swimmingPool) {
    console.log('🏊 Applying swimming pool filter');
    conditions.push(
      `facilities.ilike.%zwembad%`,
      `facilities.ilike.%pool%`,
      `facilities.ilike.%swimming%`,
      `description.ilike.%zwembad%`,
      `description.ilike.%pool%`,
      `name.ilike.%pool%`
    );
  }
  
  if (detectedFacilities.wifi) {
    console.log('📶 Applying WiFi filter');
    conditions.push(
      `facilities.ilike.%wifi%`,
      `facilities.ilike.%wi-fi%`,
      `facilities.ilike.%internet%`,
      `description.ilike.%wifi%`,
      `description.ilike.%internet%`
    );
  }
  
  if (detectedFacilities.parking) {
    console.log('🚗 Applying parking filter');
    conditions.push(
      `facilities.ilike.%parkeren%`,
      `facilities.ilike.%parking%`,
      `description.ilike.%parkeren%`,
      `description.ilike.%parking%`
    );
  }
  
  if (detectedFacilities.restaurant) {
    console.log('🍽️ Applying restaurant filter');
    conditions.push(
      `facilities.ilike.%restaurant%`,
      `facilities.ilike.%eten%`,
      `facilities.ilike.%dining%`,
      `description.ilike.%restaurant%`,
      `description.ilike.%buffet%`
    );
  }
  
  if (detectedFacilities.bar) {
    console.log('🍹 Applying bar filter');
    conditions.push(
      `facilities.ilike.%bar%`,
      `description.ilike.%bar%`,
      `name.ilike.%bar%`
    );
  }
  
  if (detectedFacilities.spa) {
    console.log('🧘 Applying spa/wellness filter');
    conditions.push(
      `facilities.ilike.%spa%`,
      `facilities.ilike.%wellness%`,
      `facilities.ilike.%sauna%`,
      `description.ilike.%spa%`,
      `description.ilike.%wellness%`,
      `name.ilike.%spa%`
    );
  }
  
  if (detectedFacilities.fitness) {
    console.log('💪 Applying fitness filter');
    conditions.push(
      `facilities.ilike.%fitness%`,
      `facilities.ilike.%gym%`,
      `description.ilike.%fitness%`,
      `description.ilike.%sportschool%`
    );
  }
  
  if (detectedFacilities.kidsClub) {
    console.log('🧒 Applying kids club filter');
    conditions.push(
      `facilities.ilike.%kinderclub%`,
      `facilities.ilike.%kids club%`,
      `description.ilike.%kinderclub%`,
      `description.ilike.%kindvriendelijk%`
    );
  }
  
  if (detectedFacilities.beachAccess) {
    console.log('🏖️ Applying beach access filter');
    conditions.push(
      `facilities.ilike.%strand%`,
      `facilities.ilike.%beach%`,
      `description.ilike.%strand%`,
      `description.ilike.%beach%`,
      `description.ilike.%zee%`
    );
  }
  
  if (detectedFacilities.airConditioning) {
    console.log('❄️ Applying air conditioning filter');
    conditions.push(
      `facilities.ilike.%airco%`,
      `facilities.ilike.%air conditioning%`,
      `description.ilike.%airco%`,
      `description.ilike.%klimaat%`
    );
  }
  
  if (conditions.length > 0) {
    console.log('✅ Applied facilities filter with conditions:', conditions.length);
    return dbQuery.or(conditions.join(','));
  }
  
  console.log('❌ No facilities conditions to apply');
  return dbQuery;
};
